import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { StudyMaterial } from "@/components/theory/PracticeRunner";
import PracticeRunner from "@/components/theory/PracticeRunner";
import { X, ChevronLeft, ChevronRight, Loader2, BookOpen, ClipboardList, CheckCircle2, Coffee, Sparkles, ExternalLink, ShieldCheck, Send } from "lucide-react";

// Ask-AI panel: student asks a question about the current topic; AI answers using only
// authoritative aviation sources and cites them.
function AskAi({ chapter, lesson, colour }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const ask = async () => {
    const question = q.trim();
    if (!question) return;
    setLoading(true); setResult(null);
    try {
      const details = (lesson?.elements || []).map((e) => `${e.title || ""}\n${e.details || ""}`).join("\n\n");
      const r = await api.post("/theory/ask", {
        question, chapter_title: chapter.title, lesson_title: lesson?.title,
        topic_details: details, lesson_id: lesson?.id,
      });
      setResult(r.data);
    } catch { toast.error("The AI tutor is busy — please try again."); } finally { setLoading(false); }
  };

  return (
    <>
      <Button variant="outline" className="mt-6 w-full sm:w-auto border-dashed" style={{ color: colour, borderColor: colour }} onClick={() => setOpen(true)} data-testid="vfrg-ask-ai-btn">
        <Sparkles className="h-4 w-4 mr-1.5" /> Ask AI about this topic
      </Button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setResult(null); } }}>
        <DialogContent className="max-w-lg" data-testid="vfrg-ask-ai-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Sparkles className="h-5 w-5" style={{ color: colour }} /> Ask AI</DialogTitle>
            <DialogDescription>Ask anything about "{lesson?.title}". Answers come only from authoritative aviation sources (CASA, CASR/MOS, AIP, VFRG) with references.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea value={q} onChange={(e) => setQ(e.target.value)} rows={3} placeholder="e.g. When do I need to carry a radio in Class G airspace?"
              data-testid="vfrg-ask-ai-input"
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) ask(); }} />
            <div className="flex justify-end">
              <Button onClick={ask} disabled={loading || !q.trim()} className="text-white font-semibold" style={{ backgroundColor: colour }} data-testid="vfrg-ask-ai-submit">
                {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Ask
              </Button>
            </div>

            {result && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="vfrg-ask-ai-answer">
                <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{result.answer}</p>
                {(result.sources || []).length > 0 ? (
                  <div className="border-t border-slate-200 pt-3">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2"><ShieldCheck className="h-3.5 w-3.5 text-lime-600" /> Authoritative sources</p>
                    <ul className="space-y-1.5">
                      {result.sources.map((s, i) => (
                        <li key={i} className="text-sm" data-testid={`vfrg-ask-ai-source-${i}`}>
                          <span className="font-medium text-slate-800">{s.name}</span>
                          {s.reference && <span className="text-slate-500"> — {s.reference}</span>}
                          {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 ml-1.5 text-sky-700 hover:underline"><ExternalLink className="h-3 w-3" /> open</a>}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 border-t border-slate-200 pt-3">No specific source cited — verify with CASA before relying on this.</p>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}


// Full-screen VFRG chapter reader: one topic per page → optional quick quiz → next topic.
export default function VfrgReader({ chapter, onClose }) {
  const colour = chapter.colour || "#0f172a";
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [mode, setMode] = useState("read");   // "read" | "quiz"
  const [quizDone, setQuizDone] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/theory/sections/${chapter.id}/lessons`);
      setLessons((r.data || []).slice().sort((a, b) => (a.seq || 0) - (b.seq || 0)));
    } catch { toast.error("Failed to load chapter"); } finally { setLoading(false); }
  }, [chapter.id]);
  useEffect(() => { load(); }, [load]);

  const lesson = lessons[idx];
  const total = lessons.length;
  const questionCount = useMemo(
    () => (lesson?.elements || []).reduce((n, e) => n + (e.questions || []).length, 0),
    [lesson]
  );

  const goTo = (n) => { setIdx(n); setMode("read"); setQuizDone(false); window.scrollTo?.(0, 0); };
  const next = () => idx < total - 1 && goTo(idx + 1);
  const prev = () => idx > 0 && goTo(idx - 1);

  return (
    <div className="fixed inset-0 z-50 bg-slate-50 flex flex-col" data-testid="vfrg-reader">
      {/* Coloured header */}
      <header className="shrink-0 text-white" style={{ backgroundColor: colour }}>
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center font-heading font-bold text-lg shrink-0">
            {(chapter.title.match(/\d+/) || ["•"])[0]}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wider text-white/70">Visual Flight Rules Guide</p>
            <h2 className="font-heading font-bold text-lg leading-tight truncate">{chapter.title}</h2>
          </div>
          <Button size="icon" variant="ghost" className="text-white hover:bg-white/20 shrink-0" onClick={onClose} data-testid="vfrg-reader-close"><X className="h-5 w-5" /></Button>
        </div>
        {total > 0 && (
          <div className="h-1.5 bg-white/20"><div className="h-full bg-white/80 transition-all" style={{ width: `${((idx + 1) / total) * 100}%` }} /></div>
        )}
      </header>

      {/* Top nav — placed above the text so it's easy to reach on mobile / tablet */}
      {!loading && total > 0 && (
        <nav className="shrink-0 border-b border-slate-200 bg-white" data-testid="vfrg-reader-nav">
          <div className="max-w-3xl mx-auto px-5 py-2.5 flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={prev} disabled={idx === 0} data-testid="vfrg-reader-prev"><ChevronLeft className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Previous</span></Button>
            <div className="flex-1 flex items-center justify-center gap-2">
              {mode === "read" && questionCount > 0 && !quizDone && (
                <Button size="sm" onClick={() => setMode("quiz")} className="text-white font-semibold" style={{ backgroundColor: colour }} data-testid="vfrg-reader-quiz-btn">
                  <ClipboardList className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Quick quiz ({questionCount})</span><span className="sm:hidden">Quiz</span>
                </Button>
              )}
              {quizDone && (
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-lime-600" data-testid="vfrg-reader-quiz-done"><CheckCircle2 className="h-4 w-4" /> <span className="hidden sm:inline">Quiz complete</span></span>
              )}
              <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-500" data-testid="vfrg-reader-break"><Coffee className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Take a break</span></Button>
            </div>
            {idx < total - 1 ? (
              <Button size="sm" onClick={next} className="bg-slate-900 hover:bg-slate-800 text-white font-semibold" data-testid="vfrg-reader-next"><span className="hidden sm:inline">Next topic</span><span className="sm:hidden">Next</span> <ChevronRight className="h-4 w-4 sm:ml-1" /></Button>
            ) : (
              <Button size="sm" onClick={onClose} className="bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold" data-testid="vfrg-reader-finish"><BookOpen className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Finish chapter</span><span className="sm:hidden">Finish</span></Button>
            )}
          </div>
        </nav>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-5 py-6">
          {loading ? (
            <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin" style={{ color: colour }} /></div>
          ) : total === 0 ? (
            <p className="text-center text-slate-400 py-24">This chapter has no topics yet.</p>
          ) : mode === "read" ? (
            <div data-testid="vfrg-reader-topic">
              <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: colour }}>Topic {idx + 1} of {total}</p>
              <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 leading-tight mb-5">{lesson.title}</h1>
              {lesson.intro && <p className="text-base text-slate-600 mb-5">{lesson.intro}</p>}
              <StudyMaterial elements={lesson.elements} />
              <AskAi chapter={chapter} lesson={lesson} colour={colour} />
            </div>
          ) : (
            <div data-testid="vfrg-reader-quiz">
              <div className="flex items-center gap-2 mb-4">
                <ClipboardList className="h-5 w-5" style={{ color: colour }} />
                <h1 className="font-heading font-bold text-xl text-slate-900">Quick quiz — {lesson.title}</h1>
              </div>
              <PracticeRunner lesson={lesson} traineeId="preview"
                onGraded={() => setQuizDone(true)}
                onClose={() => setMode("read")} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
